import { useMemo } from 'react';
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { Box, Link, Stack, Typography } from '@mui/material';
import * as turf from '@turf/turf';

import {
  ProjectStatusVariant,
  UserProject,
  UserProjectRequest,
  UserProjectSubscription,
} from 'state/userAdminSlices';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { StlButton } from 'features/common/StlButton/StlButton';
import { useProjectSlug } from 'features/common/hooks/useProjectSlug';
import { useSubscription } from 'features/common/hooks/useSubscription';
import { classNames } from 'features/common/utils';
import { MapContextProvider } from 'features/map/MapContext';
import { ProjectBoundaryViewMap } from 'features/user_admin/ProjectBoundaryViewMap';
import { ProjectStatusBadge } from 'features/user_admin/ProjectStatusBadge';
import {
  PROJECT_TAB_PARAM,
  PROJECT_TAB_SPECIAL_EVENTS,
  formatDate,
  formattedAreaFromBoundary,
  subscriptionHomeUrl,
} from 'features/user_admin/common';

import { InfoCard, InfoCardContainer } from './InfoCard';
import { ProjectTabs } from './ProjectTabs';
import SpecialDateRequestPanel from './SpecialDateRequest/SpecialDateRequestPanel';
import { UserProjectsPanel } from './UserProjectsPanel';

import styles from './UserProjectsPanel.module.css';

const kProjectStatusBlurb: { [key in ProjectStatusVariant]: string } = {
  active: 'This project is active and ready to view.',
  draft:
    'This project has been saved as a draft. Adjust as needed and submit it for review.',
  inactive:
    'This project has passed its end date and is not fetching new data.',
  fetching:
    'We’re fetching the latest data for this project. Please check back shortly.',
  pending_review:
    'Your project is currently under review. Please check back later for the updates.',
};

export function ProjectDetailPage({
  project_data,
  subscription,
}: {
  project_data: UserProjectRequest | UserProject;
  subscription: UserProjectSubscription;
}) {
  const { project_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSlug } = useProjectSlug();
  const initialTab =
    searchParams.get(PROJECT_TAB_PARAM) === PROJECT_TAB_SPECIAL_EVENTS ? 1 : 0;

  const template = subscription?.template;
  const { status, project_name, project_end, boundary } = project_data;

  const subscriptionBoundary = useMemo(() => {
    return template?.region_boundary && turf.feature(template?.region_boundary);
  }, [template]);

  const openProject = () => {
    setSlug(project_id);
    navigate(`/app`);
  };

  const canOpenProject = status.toLowerCase() === 'active';

  return (
    <>
      <Stack gap="1em" className={styles.content_block}>
        <Stack className={styles.row}>
          <Typography
            variant="h1"
            className={classNames(
              styles.txt_overflow,
              styles.subscription_name,
            )}
            title={`${subscription.subscription_name} > ${project_name}`}
          >
            <Link
              to={subscriptionHomeUrl(subscription.subscription_id)}
              className={styles.subscription_breadcrumb}
              component={RouterLink}
            >
              {subscription.subscription_name}
            </Link>{' '}
            &#8250; {project_name} <ProjectStatusBadge status={status} />
          </Typography>
          <Stack direction="row" gap="1em">
            <StlButton
              handleClick={() =>
                navigate(subscriptionHomeUrl(subscription.subscription_id))
              }
              value="Subscription Details"
              width={170}
              variant="text"
            />

            {canOpenProject && (
              <StlButton
                value="Open Project"
                handleClick={openProject}
                brown
                icon={
                  <FontAwesomeIcon
                    name="arrow-up-right-from-square"
                    size="12px"
                  />
                }
                width={121}
                sx={{ justifyContent: 'flex-start !important' }}
              />
            )}
          </Stack>
        </Stack>
        <Typography>{kProjectStatusBlurb[status.toLowerCase()]}</Typography>
        <InfoCardContainer>
          <InfoCard
            num={formatDate(project_end) || 'Not set'}
            metric="End date"
          />
          <InfoCard
            num={
              boundary
                ? formattedAreaFromBoundary(boundary, subscription)
                : 'Unknown'
            }
            metric="Project Area"
          />
        </InfoCardContainer>
      </Stack>
      <Box className={styles.tabsWrapper}>
        <ProjectTabs version="detail" initialTab={initialTab}>
          <Stack sx={{ height: '100%' }}>
            <MapContextProvider>
              <ProjectBoundaryViewMap
                boundary={boundary}
                subscriptionBoundary={subscriptionBoundary}
              />
            </MapContextProvider>
          </Stack>
          <SpecialDateRequestPanel />
        </ProjectTabs>
      </Box>
    </>
  );
}

export function ProjectDetail() {
  const { subscription, project_data } = useSubscription();

  if (!project_data) return null;
  return (
    <UserProjectsPanel>
      <ProjectDetailPage
        project_data={project_data}
        subscription={subscription}
      />
    </UserProjectsPanel>
  );
}
